/*
 * ------------ Theme Common JS ------------
 */

if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

const PUB_SUB_EVENTS = {
  cartUpdate: "cart-update",
  quantityUpdate: "quantity-update",
  variantChange: "variant-change",
  cartError: "cart-error",
};

let subscribers = {};

function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = [];
  }

  subscribers[eventName] = [...subscribers[eventName], callback];

  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}

function publish(eventName, data) {
  if (subscribers[eventName]) {
    subscribers[eventName].forEach((callback) => {
      callback(data);
    });
  }
}

// Shared script handles appearance animations across sections (Multicolumn, Media banner, Banner grid, etc.), adding the init-appearance-animation class when a section enters the viewport.
function setAppearanceClass() {
  const sections = document.querySelectorAll(
    "[data-has-appearance-animation='true']"
  );

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("init-appearance-animation");
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.01 }
  );

  sections.forEach((section) => observer.observe(section));
}

function calculateAndUpdateOffset(sliderEl) {
  const container = document.querySelector(".container");
  if (!container) return 0;

  const styles = getComputedStyle(container);
  const paddingLeft = parseFloat(styles.paddingLeft);
  const offset = container.getBoundingClientRect().left + paddingLeft;

  sliderEl.style.setProperty("--left-space-before-init", `${offset}px`);

  return offset;
}

// Shared initialization script initSectionSliderLayout is used for sliders across sections (Popular products, Featured blog, Collection list, Multicolumn, etc.). Sliders can be initialized per section if needed.
function initSectionSliderLayout(section) {
  if (!section.closest("[data-section-has-card-slider='true']")) return;

  const sliderEl = section.querySelector(".js-section-card-slider");
  if (!sliderEl) return;

  const paginationEl = section.querySelector(".slider-pagination");
  const nextBtn = section.querySelector(".slider-nav-button--next");
  const prevBtn = section.querySelector(".slider-nav-button--prev");
  const hasNavigation = sliderEl.getAttribute("data-navigation") === "true";
  const hasPagination = sliderEl.getAttribute("data-pagination") === "true";
  const controlsEl = section.querySelector(".slider-controls");
  const isSpinnerPagination = !!controlsEl?.querySelector(
    ".slider-controls__pagination"
  );
  const sliderFromTestimonials =
    sliderEl.getAttribute("data-testimonials") === "true";

  const sliderLoop = sliderEl.getAttribute("data-loop") === "true";
  const hasSliderAutoplay = sliderEl.getAttribute("data-autoplay") === "true";
  const pauseAutoplayOnHover =
    sliderEl.getAttribute("data-autoplay-pause-on-hover") === "true";
  const autoplayDelay = Number(sliderEl.dataset.autoplayDelay || 8000);
  const slidesPerRow = Number(sliderEl.dataset.slidesPerRow || 4);
  const slidesPerRowMobile = Number(sliderEl.dataset.slidesPerRowMobile || 1);

  const slidesPerRow576 = sliderFromTestimonials
    ? "auto"
    : slidesPerRow > 1
      ? 2
      : 1;
  const slidesPerRow990 = sliderFromTestimonials
    ? "auto"
    : slidesPerRow > 2
      ? slidesPerRow576 + 1
      : slidesPerRow576;
  const slidesPerRow1200 = sliderFromTestimonials
    ? "auto"
    : slidesPerRow >= 4
      ? 4
      : slidesPerRow;

  const offset = sliderFromTestimonials
    ? calculateAndUpdateOffset(sliderEl)
    : 0;

  const sliderSettings = {
    loop: sliderLoop,
    slidesPerView: sliderFromTestimonials ? 1 : slidesPerRowMobile,
    slidesOffsetBefore: 0,
    slidesOffsetAfter: 0,
    spaceBetween: sliderFromTestimonials ? 20 : 16,
    speed: 800,
    watchSlideProgress: true,
    allowTouchMove: true,
    mousewheel: {
      forceToAxis: true,
    },
    a11y: {
      scrollOnFocus: true,
    },
    autoplay: hasSliderAutoplay
      ? {
          delay: autoplayDelay,
          disableOnInteraction: false,
          pauseOnMouseEnter: pauseAutoplayOnHover,
        }
      : false,
    pagination: hasPagination
      ? {
          el: paginationEl,
          clickable: true,
          ...(isSpinnerPagination
            ? {
                renderBullet(_index, className) {
                  return `<span class="${className}"><svg class="bullet-spinner" viewBox="0 0 14 14" aria-hidden="true" focusable="false"><circle class="bullet-spinner__track" cx="7" cy="7" r="5"/><circle class="bullet-spinner__arc" cx="7" cy="7" r="5" transform="rotate(-90 7 7)"/></svg></span>`;
                },
              }
            : {}),
        }
      : false,
    navigation: hasNavigation
      ? {
          nextEl: nextBtn,
          prevEl: prevBtn,
          disabledClass: "swiper-button-disabled",
        }
      : false,
    breakpoints: {
      576: {
        slidesPerView: slidesPerRow576,
        spaceBetween: 16,
      },
      990: {
        slidesPerView: slidesPerRow990,
        spaceBetween: 16,
        slidesOffsetBefore: sliderFromTestimonials ? offset : 0,
        slidesOffsetAfter: sliderFromTestimonials ? offset : 0,
      },
      1200: {
        slidesPerView: slidesPerRow1200,
        spaceBetween: 16,
        slidesOffsetBefore: sliderFromTestimonials ? offset : 0,
        slidesOffsetAfter: sliderFromTestimonials ? offset : 0,
      },
    },
    on: {
      autoplayTimeLeft(s, time, progress) {
        if (hasPagination && paginationEl) {
          paginationEl.style.setProperty("--slider-progress", 1 - progress);

          if (isSpinnerPagination) {
            const arc = paginationEl.querySelector(
              ".swiper-pagination-bullet-active .bullet-spinner__arc"
            );
            if (arc) arc.style.strokeDashoffset = 31.416 * progress;
          }
        }
      },
      slideChange() {
        if (isSpinnerPagination && paginationEl) {
          paginationEl
            .querySelectorAll(".bullet-spinner__arc")
            .forEach((arc) => {
              arc.style.strokeDashoffset = "";
            });
        }
      },
      autoplayPause() {
        if (controlsEl) controlsEl.setAttribute("data-autoplay-paused", "");
      },
      autoplayResume() {
        if (controlsEl) controlsEl.removeAttribute("data-autoplay-paused");
      },
      lock() {
        if (controlsEl) controlsEl.setAttribute("data-slider-locked", "");
      },
      unlock() {
        if (controlsEl) controlsEl.removeAttribute("data-slider-locked");
      },
      touchStart() {
        section.classList.add("section-card-slider--touching");
        sliderEl.classList.add("section-card-slider--touching");
      },
      touchEnd() {
        section.classList.remove("section-card-slider--touching");
        sliderEl.classList.remove("section-card-slider--touching");
      },
      transitionEnd() {
        section.classList.remove("section-card-slider--touching");
        sliderEl.classList.remove("section-card-slider--touching");
      },
    },
  };

  new Swiper(sliderEl, sliderSettings);

  if (controlsEl) {
    controlsEl.toggleAttribute(
      "data-slider-locked",
      !!sliderEl.swiper?.isLocked
    );
  }

  if (hasSliderAutoplay && sliderEl.swiper) {
    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          sliderEl.swiper.autoplay.resume();
        } else {
          sliderEl.swiper.autoplay.pause();
        }
      });
    });

    sectionObserver.observe(section);
  }

  if (sliderFromTestimonials) {
    let resizeWidth;

    window.addEventListener("resize", () => {
      clearTimeout(resizeWidth);
      resizeWidth = setTimeout(() => {
        if (sliderEl?.swiper) {
          const offset = calculateAndUpdateOffset(sliderEl);
          if (window.innerWidth > 990) {
            sliderEl.swiper.params.slidesOffsetBefore = offset;
            sliderEl.swiper.params.slidesOffsetAfter = offset;
          } else {
            sliderEl.swiper.params.slidesOffsetBefore = 0;
            sliderEl.swiper.params.slidesOffsetAfter = 0;
          }
          sliderEl.swiper.update();
        }
      }, 100);
    });
  }
}

document.addEventListener("DOMContentLoaded", () => {
  const sectionsWithCardSlider = document.querySelectorAll(
    "[data-section-has-card-slider='true']"
  );
  sectionsWithCardSlider.forEach(initSectionSliderLayout);

  setAppearanceClass();
});

document.addEventListener("shopify:section:load", function (event) {
  if (typeof event.target?.querySelector !== "function") return;

  const section = event.target?.querySelector(
    "[data-section-has-card-slider='true']"
  );
  if (section) initSectionSliderLayout(section);

  setAppearanceClass();
});

if (navigator.userAgent.indexOf("iPhone") > -1) {
  document
    .querySelector("[name=viewport]")
    .setAttribute(
      "content",
      "width=device-width, initial-scale=1, maximum-scale=1"
    );
}

function lockScrollBodyOnIos(openScrollY) {
  if (navigator.userAgent.indexOf("iPhone") > -1) {
    document.documentElement.style.setProperty(
      "--current-body-scroll",
      `${-1 * openScrollY}px`
    );
    document.documentElement.style.setProperty("scroll-behavior", "unset");
    document.body.classList.add("forced-scroll-locked");
  }
}

function unlockScrollBodyOnIos(openScrollY) {
  if (navigator.userAgent.indexOf("iPhone") > -1) {
    document.body.classList.remove("forced-scroll-locked");
    document.documentElement.scrollTop = openScrollY;
    document.documentElement.style.removeProperty("--current-body-scroll");
    document.documentElement.style.removeProperty("scroll-behavior");
  }
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  ).filter((element) => element.offsetParent !== null);
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute("aria-expanded", "false");

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute(
      "aria-expanded",
      !event.currentTarget.closest("details").hasAttribute("open")
    );
  });

  if (summary.closest("header-mobile-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  removeTrapFocus();

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key

    // Recompute on every Tab — modals load content (installments, dynamic
    // checkout) after the trap is set, so a cached list goes stale.
    const elements = getFocusableElements(container);
    if (!elements.length) return;

    const first = elements[0];
    const last = elements[elements.length - 1];
    const target = event.target;

    if (!container.contains(target)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }

    // Shadow-DOM hosts (payment terms, dynamic checkout) retarget events to the
    // host, which is absent from the list — hijack Tab only at real boundaries
    // and let the browser walk everything in between; focusin covers escapes.
    if (event.shiftKey) {
      if (target === first || target === container) {
        event.preventDefault();
        last.focus();
      }
    } else if (target === last || target === container) {
      event.preventDefault();
      first.focus();
    }
  };

  trapFocusHandlers.focusin = (event) => {
    if (container.contains(event.target)) return;

    const elements = getFocusableElements(container);
    elements[0]?.focus();
  };

  document.addEventListener("keydown", trapFocusHandlers.keydown, true);
  document.addEventListener("focusin", trapFocusHandlers.focusin, true);

  elementToFocus?.focus();
}

function pauseAllMedia(parentElement = document, exceptElement = null) {
  parentElement.querySelectorAll(".js-youtube").forEach((video) => {
    if (exceptElement && exceptElement.contains(video)) return;
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + "pauseVideo" + '","args":""}',
      "*"
    );
  });
  parentElement.querySelectorAll(".js-vimeo").forEach((video) => {
    if (exceptElement && exceptElement.contains(video)) return;
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  parentElement.querySelectorAll("video").forEach((video) => {
    if (exceptElement && exceptElement.contains(video)) return;
    video.pause();
  });
  parentElement.querySelectorAll("product-model").forEach((model) => {
    if (exceptElement && exceptElement.contains(model)) return;
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin, true);
  document.removeEventListener("keydown", trapFocusHandlers.keydown, true);

  if (elementToFocus) elementToFocus.focus();
}

function formatMoney(cents, format = "") {
  if (typeof cents === "string") {
    cents = cents.replace(".", "");
  }
  cents = parseInt(cents, 10);

  let value = "";
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || theme.moneyFormat;

  function formatWithDelimiters(
    number,
    precision = 2,
    thousands = ",",
    decimal = "."
  ) {
    if (isNaN(number) || number == null) {
      return "0";
    }

    number = (number / 100.0).toFixed(precision);

    const parts = number.split(".");
    const dollarsAmount = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      `$1${thousands}`
    );
    const centsAmount = parts[1] ? decimal + parts[1] : "";

    return dollarsAmount + centsAmount;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2, ",", ".");
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0, ",", ".");
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
    case "amount_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 2, "'", ".");
      break;
    case "amount_no_decimals_with_space_separator":
      value = formatWithDelimiters(cents, 0, " ", ".");
      break;
    case "amount_with_space_separator":
      value = formatWithDelimiters(cents, 2, " ", ",");
      break;
    case "amount_with_period_and_space_separator":
      value = formatWithDelimiters(cents, 2, " ", ".");
      break;
    default:
      value = formatWithDelimiters(cents, 2, ",", ".");
  }

  return formatString.replace(placeholderRegex, value);
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

(function () {
  function copyURI(e, copyLink) {
    e.preventDefault();

    const url = window.location.href;

    const handleCopy = (btn, success) => {
      if (success) {
        btn.classList.add("copied");
        setTimeout(() => btn.classList.remove("copied"), 1000);
      } else {
        btn.classList.add("notCopied");
        setTimeout(() => btn.classList.remove("notCopied"), 1000);
      }
    };

    // Modern Clipboard API
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(url)
        .then(() => handleCopy(copyLink, true))
        .catch(() => handleCopy(copyLink, false));
    } else {
      // Fallback for unsupported browsers
      const textArea = document.createElement("textarea");
      textArea.value = url;
      textArea.style.position = "fixed"; // avoid scrolling
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        const successful = document.execCommand("copy");
        handleCopy(copyLink, successful);
      } catch (err) {
        handleCopy(copyLink, false);
      }

      document.body.removeChild(textArea);
    }
  }

  const copyLinks = document.querySelectorAll(".copy-btn");
  copyLinks.forEach((copyLink) =>
    copyLink.addEventListener("click", (e) => copyURI(e, copyLink))
  );
})();

document.addEventListener("click", (e) => {
  const toggle = e.target.closest(".block-share__toggle");

  if (toggle) {
    const share = toggle.closest(".block-share--expandable");
    const panel = share.querySelector(".block-share__panel");
    const isOpen = panel.classList.contains("is-open");
    panel.classList.toggle("is-open", !isOpen);
    panel.setAttribute("aria-hidden", String(isOpen));
    toggle.setAttribute("aria-expanded", String(!isOpen));
    return;
  }

  if (!e.target.closest(".block-share--expandable")) {
    document
      .querySelectorAll(".block-share__panel.is-open")
      .forEach((panel) => {
        panel.classList.remove("is-open");
        panel.setAttribute("aria-hidden", "true");
        const share = panel.closest(".block-share--expandable");
        if (share)
          share
            .querySelector(".block-share__toggle")
            ?.setAttribute("aria-expanded", "false");
      });
  }
});

if (!Element.prototype.replaceChildren) {
  Element.prototype.replaceChildren = function (...nodes) {
    while (this.firstChild) this.removeChild(this.firstChild);
    this.append(...nodes);
  };
}

// [Theme custom event] - Cart UI refresh event
document.addEventListener("cart:refresh", (event) => {
  const sectionsToUpdate = [
    {
      id: "cart-icon-bubble",
      selector: ".cart-count-bubble",
    },
    {
      id: "cart-drawer",
      selector: "#CartDrawer",
    },
    {
      id: "main-cart",
      selector: ".main-cart",
    },
  ];

  const sectionIds = sectionsToUpdate.map((section) => section.id).join(",");

  fetch(`${routes.cart_url}?sections=${sectionIds}`)
    .then((response) => response.json())
    .then((sections) => {
      sectionsToUpdate.forEach((section) => {
        const sectionHTML = sections[section.id];
        if (!sectionHTML) return;

        const html = new DOMParser().parseFromString(sectionHTML, "text/html");
        const selector = section.selector;

        const sourceElement = html.querySelector(selector);
        const destinationElements = document.querySelectorAll(selector);

        if (sourceElement && destinationElements.length > 0) {
          destinationElements.forEach((destinationElement) => {
            destinationElement.innerHTML = sourceElement.innerHTML;
            if (sourceElement.classList.contains("is-empty")) {
              destinationElement.classList.add("is-empty");
            } else {
              destinationElement.classList.remove("is-empty");
            }
          });
        }
      });
    })
    .catch((error) => {
      console.error("Error updating cart:", error);
    });
});

/*
 * ------------ Theme Web Components ------------
 */

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.changeEvent = new Event("change", { bubbles: true });
    this._initialized = false;
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.input = this.querySelector("input");
    this.isCartItem = Boolean(this.closest(".cart-item"));

    this.querySelectorAll("button").forEach((button) => {
      button.addEventListener("click", this.onButtonClick.bind(this));
    });

    this.setMinimumDisable();

    var eventList = ["paste", "input"];
    for (const event of eventList) {
      this.input.addEventListener(event, (e) => {
        const value = e.currentTarget.value;
        const numberRegex = this.isCartItem ? /^\d*$/ : /^0*?[1-9]\d*$/;

        if (numberRegex.test(value) || value === "") {
        } else {
          e.currentTarget.value = 1;
        }

        this.setMinimumDisable();
      });
    }

    this.input.addEventListener("focusout", (e) => {
      if (e.currentTarget.value === "") {
        e.currentTarget.value = 1;
        this.setMinimumDisable();
      }
    });
  }

  setMinimumDisable() {
    const minusBtn = this.querySelector('button[name="minus"]');
    if (!minusBtn || !this.input) return;

    if (this.input.value == 1) {
      minusBtn.classList.add("disabled");
    } else {
      minusBtn.classList.remove("disabled");
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === "plus" ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) {
      this.input.dispatchEvent(this.changeEvent);
    }

    this.setMinimumDisable();
  }
}

if (!customElements.get("quantity-input")) {
  customElements.define("quantity-input", QuantityInput);
}

class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetBtn = this.querySelector(".js-search-input-reset");

    this.input?.form?.addEventListener("reset", this.onFormReset.bind(this));
    this.input?.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );

    this.resetBtn?.addEventListener("click", this.onFormReset.bind(this));
  }

  connectedCallback() {
    this.toggleResetBtn(this.input?.value);
  }

  toggleResetBtn(value) {
    if (!this.resetBtn) return;

    if (value) {
      this.resetBtn.style.display = "flex";
    } else {
      this.resetBtn.style.display = "none";
    }
  }

  onChange(event) {
    this.toggleResetBtn(event.target.value);
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();

    if (this.input) {
      if (this.closest(".search-section") && window.location.search) {
        const params = new URLSearchParams(window.location.search);
        const q = params.get("q");
        if (this.input.value !== q) {
          setTimeout(() => {
            this.input.value = q;
            this.input.focus();
            this.toggleResetBtn(this.input.value);
          }, 0);
        } else {
          const cleanURL = window.location.pathname;
          window.location.href = cleanURL;
        }
      } else {
        this.input.value = "";
        this.input.focus();
        this.toggleResetBtn("");
      }
    }
  }
}

if (!customElements.get("search-form")) {
  customElements.define("search-form", SearchForm);
}

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");
    this.closeBtn = this.querySelector(".modal-close-button");
    const summaryElements = this.querySelectorAll("summary");
    this.addAccessibilityAttributes(summaryElements);

    this.headerWrapper = document.querySelector(".header-wrapper");
    if (this.headerWrapper) this.headerWrapper.preventHide = false;

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.onSummaryClick.bind(this))
    );

    this.closeBtn?.addEventListener(
      "click",
      this.onCloseButtonClick.bind(this)
    );
  }

  addAccessibilityAttributes(summaryElements) {
    summaryElements.forEach((element) => {
      element.setAttribute("role", "button");
      element.setAttribute("aria-expanded", false);
      element.setAttribute("aria-controls", element.nextElementSibling.id);
    });
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(this.mainDetailsToggle.querySelector("summary"))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const isOpen = detailsElement.hasAttribute("open");

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeMenuDrawer(summaryElement)
        : this.openMenuDrawer(summaryElement);
    } else {
      trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector("button")
      );

      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
      });
    }
  }

  openMenuDrawer(summaryElement) {
    if (this.headerWrapper) this.headerWrapper.preventHide = true;
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-drawer`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event !== undefined) {
      this.mainDetailsToggle.classList.remove("menu-opening");
      this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
        details.removeAttribute("open");
        details.classList.remove("menu-opening");
      });
      this.mainDetailsToggle
        .querySelector("summary")
        .setAttribute("aria-expanded", false);
      document.body.classList.remove(`overflow-hidden-drawer`);
      removeTrapFocus(elementToFocus);
      this.closeAnimation(this.mainDetailsToggle);
      if (this.headerWrapper) this.headerWrapper.preventHide = false;
    }
  }

  onFocusOut(event) {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute("open") &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    if (!detailsElement) return;
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    detailsElement?.classList.remove("menu-opening");
    removeTrapFocus();
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(
            detailsElement.closest("details[open]"),
            detailsElement.querySelector("summary")
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

if (!customElements.get("menu-drawer")) {
  customElements.define("menu-drawer", MenuDrawer);
}

class HeaderMobileDrawer extends MenuDrawer {
  constructor() {
    super();
    this.header = document.querySelector(".shopify-section-header");
    this.headerWrapper = document.querySelector(".header-wrapper");
    if (this.headerWrapper) this.headerWrapper.preventHide = false;
    this.openBtn = this.querySelector(".js-menu-drawer-open");
    this.closeBtn = this.querySelector(".js-menu-drawer-close");
    this.isOpen = false;

    this.closeBtn?.addEventListener("click", () => {
      this.closeMenuDrawer(this.openBtn);
    });
  }

  openMenuDrawer(summaryElement) {
    if (this.headerWrapper) this.headerWrapper.preventHide = true;
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add("overflow-hidden-mobile-menu");
    this.isOpen = true;
    this.openScrollY = window.scrollY;

    // styles for forced disable body scroll on iOS
    if (typeof window.lockScrollBodyOnIos === "function") {
      this.openScrollY = window.scrollY;
      window.lockScrollBodyOnIos(this.openScrollY);
    }
  }

  closeMenuDrawer(elementToFocus) {
    if (!elementToFocus) return;
    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    document.body.classList.remove("overflow-hidden-mobile-menu");
    document.body.classList.remove("overflow-hidden");
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    // close mega menus mobile if open
    const megaSubmenuLinks = this.querySelectorAll(
      ".header-mobile-menu__item--megasubmenu"
    );
    if (megaSubmenuLinks && megaSubmenuLinks.length) {
      megaSubmenuLinks.forEach((link) => {
        const megaSubmenuEl = link.querySelector(".mega-submenu-mobile");
        if (!megaSubmenuEl) return;
        megaSubmenuEl.classList.remove("mega-submenu-mobile--visible");
      });
    }

    // close search modal if open
    const searchModal = document.querySelector(".header-search-modal");
    if (searchModal && searchModal.getAttribute("open")) {
      const headerSearch = document.querySelector("header-search");
      if (headerSearch && typeof headerSearch.close === "function") {
        headerSearch.close(null, elementToFocus);
      }
    }

    // styles for forced disable body scroll on iOS

    if (typeof window.unlockScrollBodyOnIos === "function") {
      window.unlockScrollBodyOnIos(this.openScrollY);
    }

    if (this.headerWrapper) this.headerWrapper.preventHide = false;
    this.isOpen = false;
  }
}

if (!customElements.get("header-mobile-drawer")) {
  customElements.define("header-mobile-drawer", HeaderMobileDrawer);
}

class BurgerMenu extends MenuDrawer {
  constructor() {
    super();
    this.openBtn = this.querySelector(".js-burger-menu-open");
    this.closeBtn = this.querySelector(".js-burger-menu-close");

    this.closeBtn?.addEventListener("click", () => {
      this.closeMenuDrawer(this.openBtn);
    });
  }
}

if (!customElements.get("burger-menu")) {
  customElements.define("burger-menu", BurgerMenu);
}

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll(
      '[id^="ModalCloseBtn-"], [id^="ModalCloseOverlay-"]'
    ).forEach((el) => el.addEventListener("click", () => this.hide(false)));

    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (
          event.pointerType === "mouse" &&
          !event.target.closest("deferred-media, product-model")
        ) {
          this.hide();
        }
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    this.addEventListener(
      "transitionend",
      () => {
        if (this.hasAttribute("open")) {
          trapFocus(this, this.querySelector('[role="dialog"]'));
        }
      },
      { once: true }
    );
  }

  hide() {
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    document.body.classList.remove("overflow-hidden");
  }
}

if (!customElements.get("modal-dialog")) {
  customElements.define("modal-dialog", ModalDialog);
}

class ProductModal extends ModalDialog {
  constructor() {
    super();
  }
}

if (!customElements.get("product-modal")) {
  customElements.define("product-modal", ProductModal);
}

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", () => {
      if (window.innerWidth >= 990 && this.dataset.desktopInlineZoom === "true")
        return;
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}

if (!customElements.get("modal-opener")) {
  customElements.define("modal-opener", ModalOpener);
}

class DeferredMedia extends HTMLElement {
  constructor() {
    super();

    this.init();
  }

  init() {
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (poster) {
      poster.addEventListener("click", this.loadContent.bind(this));
    }

    if (this.dataset.autoplay === "true") {
      this.loadContent();
    }
  }

  loadContent() {
    const parentEl =
      this.closest(".product__media") || this.closest(".product-media-modal");
    pauseAllMedia(parentEl);

    if (this.getAttribute("loaded")) return;

    const template = this.querySelector("template");
    if (!template) return;

    const content = document.createElement("div");
    content.appendChild(template.content.firstElementChild.cloneNode(true));
    this.setAttribute("loaded", true);

    const media = content.querySelector("video, model-viewer, iframe");
    if (!media) return;

    const deferredElement = this.appendChild(media);

    if (["VIDEO", "IFRAME"].includes(deferredElement.nodeName)) {
      const videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            let shouldAutoplay = false;
            const parentSlide = entry.target.closest(".swiper-slide");
            if (parentSlide) {
              if (parentSlide.classList.contains("swiper-slide-active")) {
                shouldAutoplay = true;
              }
            } else {
              if (entry.intersectionRatio > 0.7) {
                shouldAutoplay = true;
              }
            }

            if (shouldAutoplay) {
              if (
                (this.dataset.autoplay === "true" &&
                  !this.closest("product-modal")) ||
                (this.dataset.autoplay === "true" &&
                  this.closest("product-modal")?.hasAttribute("open"))
              ) {
                pauseAllMedia(parentEl, this);

                if (entry.target.nodeName === "VIDEO") {
                  entry.target.play()?.catch((error) => {
                    console.error("Error playing video:", error);
                  });
                } else if (
                  entry.target.nodeName === "IFRAME" &&
                  entry.target.contentWindow
                ) {
                  if (entry.target.classList.contains("js-youtube")) {
                    entry.target.contentWindow.postMessage(
                      '{"event":"command","func":"playVideo","args":""}',
                      "*"
                    );
                  } else {
                    entry.target.contentWindow.postMessage(
                      '{"method":"play"}',
                      "*"
                    );
                  }
                }
              }
            } else {
              if (entry.target.nodeName === "VIDEO") {
                entry.target.pause();
              } else if (
                entry.target.nodeName === "IFRAME" &&
                entry.target.contentWindow
              ) {
                if (entry.target.classList.contains("js-youtube")) {
                  entry.target.contentWindow.postMessage(
                    '{"event":"command","func":"pauseVideo","args":""}',
                    "*"
                  );
                } else {
                  entry.target.contentWindow.postMessage(
                    '{"method":"pause"}',
                    "*"
                  );
                }
              }
            }
          });
        },
        {
          threshold: Array.from({ length: 1000 }, (_, i) => i / 1000),
        }
      );

      videoObserver.observe(deferredElement);
    }

    const modelViewer = this.closest(".swiper-slide-active")?.querySelector(
      "model-viewer"
    );
    if (
      modelViewer &&
      !modelViewer.classList.contains("shopify-model-viewer-ui__disabled")
    ) {
      const swiper = this.closest(".swiper")?.swiper;
      if (swiper) {
        swiper.params.noSwiping = true;
        swiper.params.noSwipingClass = "swiper-slide";
      }
    }

    this.querySelectorAll(
      "video, .shopify-model-viewer-ui__controls-overlay"
    ).forEach((media) => {
      if (this.getAttribute("loaded") === "true") {
        media.addEventListener("click", (event) => {
          pauseAllMedia(parentEl, this);
        });
      }
    });
  }
}

if (!customElements.get("deferred-media")) {
  customElements.define("deferred-media", DeferredMedia);
}

class ProductModel extends DeferredMedia {
  constructor() {
    super();
  }

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: "model-viewer-ui",
        version: "1.0",
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  setupModelViewerUI(errors) {
    if (errors) return;

    this.modelViewerUI = new Shopify.ModelViewerUI(
      this.querySelector("model-viewer")
    );

    const $this = this;
    const target = $this.querySelector("model-viewer");

    const observer = new MutationObserver((records) => {
      if ($this.closest(".swiper") && $this.closest(".swiper").swiper) {
        const swiperObj = $this.closest(".swiper").swiper;
        if (!target.classList.contains("shopify-model-viewer-ui__disabled")) {
          swiperObj.params.noSwiping = true;
          swiperObj.params.noSwipingClass = "swiper-slide";
        } else {
          swiperObj.params.noSwiping = false;
        }
      }
    });

    observer.observe(target, { attributes: true });
  }
}

if (!customElements.get("product-model")) {
  customElements.define("product-model", ProductModel);
}

window.ProductModel = {
  loadShopifyXR() {
    Shopify.loadFeatures([
      {
        name: "shopify-xr",
        version: "1.0",
        onLoad: this.setupShopifyXR.bind(this),
      },
    ]);
  },

  setupShopifyXR(errors) {
    if (errors) return;

    if (!window.ShopifyXR) {
      document.addEventListener("shopify_xr_initialized", () =>
        this.setupShopifyXR()
      );
      return;
    }

    document.querySelectorAll('[id^="ProductJSON-"]').forEach((modelJSON) => {
      window.ShopifyXR.addModels(JSON.parse(modelJSON.textContent));
      modelJSON.remove();
    });
    window.ShopifyXR.setupXRElements();
  },
};

window.addEventListener("DOMContentLoaded", () => {
  window.ProductModel?.loadShopifyXR();
});

class VariantPicker extends HTMLElement {
  constructor() {
    super();
    this._initialized = false;
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.addEventListener("change", this.onVariantChange.bind(this));

    this.variantMediaDisplay =
      this.closest(".product")?.querySelector(".product__media")?.dataset
        ?.variantMediaDisplay || "show_all";
  }

  onVariantChange(event) {
    if (!this.contains(event.target)) return;

    //this.connectedProductURL = event.target.dataset?.connectedProductUrl;
    const selectedValuesIds = this.getSelectedValuesIds();
    const requestUrl = this.createRequestUrl({
      selectedValuesIds: selectedValuesIds,
      connectedProductURL: this.connectedProductURL,
    });

    this.fetchProductInfo(requestUrl);

    this.resetErrorMessage();
  }

  getSelectedValuesIds() {
    const controls = [...this.querySelectorAll(".product-form__controls")];

    controls.sort((a, b) => {
      return (
        Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition)
      );
    });

    // Recomputed from scratch on every call so switching back to an option
    // combination with no connected product clears out a stale URL from a
    // previous combined-listing selection.
    this.connectedProductURL = undefined;

    return controls.map((control) => {
      const checkedInput = control.querySelector('input[type="radio"]:checked');

      // Supporting combined listings: If the checked input has a connected product url, save it to use in the request
      if (checkedInput.dataset?.connectedProductUrl) {
        this.connectedProductURL = checkedInput.dataset.connectedProductUrl;
      }
      return checkedInput?.dataset?.optionValueId
        ? checkedInput.dataset.optionValueId
        : null;
    });
  }

  createRequestUrl({ selectedValuesIds = [], connectedProductURL = "" }) {
    const productUrl = connectedProductURL || `${this.dataset.url}`;
    const sectionId = this.dataset.originalSection
      ? this.dataset.originalSection
      : this.dataset.section;

    if (selectedValuesIds.length) {
      const params = [];
      params.push(`section_id=${sectionId}`);
      params.push(`option_values=${selectedValuesIds.join(",")}`);
      return `${productUrl}?${params.join("&")}`;
    }
  }

  fetchProductInfo(requestUrl) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    fetch(requestUrl, { signal: this.abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, "text/html");

        try {
          // Search this.currentVariant
          this.setCurrentVariantAfterFetch(html);

          this.updateElementsAfterFetch(html);

          this.updateURL();
          this.updateVariantInput();
          this.updatePickupAvailability();

          if (
            this.currentVariant.featured_media &&
            this.variantMediaDisplay === "show_all"
          ) {
            // If variant display != "show_all", the media gallery element is fully replaced inside updateElementsAfterFetch
            const mediaId = `${this.dataset.section}-${this.currentVariant.featured_media.id}`;
            this.updateMedia(mediaId);
          }
        } catch (err) {}

        if (!this.currentVariant) {
          this.setUnavailable();
        } else {
          this.toggleAddButton(
            !this.currentVariant.available,
            window.variantStrings.soldOut
          );
        }
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          console.info("Fetch aborted by user");
        } else {
          console.error(error);
        }
      })
      .finally(() => {});
  }

  setCurrentVariantAfterFetch(html) {
    // attr data-original-section use for Quick view modal
    const sourceSectionId = this.dataset.originalSection
      ? this.dataset.originalSection
      : this.dataset.section;

    const variantPickerSource = html.querySelector(
      `[data-id="VariantPicker-${sourceSectionId}"]`
    );
    if (!variantPickerSource) return;

    const newVariantDataEl = variantPickerSource.querySelector(
      "[data-selected-variant]"
    );
    if (!newVariantDataEl) return;

    const newVariantData = variantPickerSource.querySelector(
      "[data-selected-variant]"
    ).innerHTML;

    const selectedVariant = !!newVariantData
      ? JSON.parse(newVariantData)
      : null;

    this.currentVariant = selectedVariant;
  }

  updateURL() {
    if (this.dataset.updateUrl === "false") return;
    const productUrl = this.connectedProductURL || this.dataset.url;
    const newUrl = this.currentVariant
      ? `${productUrl}?variant=${this.currentVariant.id}`
      : productUrl;

    window.history.replaceState({}, "", newUrl);
  }

  updateVariantInput() {
    if (!this.currentVariant?.id) return;

    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, #sticky-bar-product-form-${this.dataset.section}`
    );
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });

    publish(PUB_SUB_EVENTS.variantChange, {
      data: {
        sectionId: this.dataset.section,
        variant: this.currentVariant,
        productUrl: this.connectedProductURL || this.dataset.url,
      },
    });

    // [Start theme custom event] - Variant change event
    document.dispatchEvent(
      new CustomEvent("variant:change", {
        detail: {
          variant: this.currentVariant,
          previousVariant: this.previousVariant || null,
          formElement: this,
          sectionId: this.dataset.section,
          productUrl: this.connectedProductURL || this.dataset.url,
        },
      })
    );

    this.previousVariant = this.currentVariant;
    // [End theme custom event] - Variant change event
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelectorAll("pickup-availability");
    if (!pickUpAvailability) return;
    pickUpAvailability.forEach((el) => {
      if (this.currentVariant && this.currentVariant.available) {
        el.fetchAvailability(this.currentVariant.id);
      } else {
        el.removeAttribute("available");
        el.innerHTML = "";
      }
    });
  }

  updateMedia(mediaId) {
    if (!mediaId) return;

    const mediaEl = document.querySelector(
      `[data-section="product-media-${this.dataset.section}"]`
    );
    if (!mediaEl) return;

    // for slider and slider_previews layout
    const sliderEl = mediaEl.querySelector(".js-media-list");
    if (sliderEl && sliderEl.swiper) {
      sliderEl.querySelectorAll(".swiper-slide").forEach((slide, index) => {
        const slideMediaId = slide.dataset?.mediaId;
        if (slideMediaId === mediaId) {
          const findIndex = sliderEl.swiper.slides.findIndex((slideEl) => {
            return slideEl.dataset?.mediaId === slideMediaId;
          });
          const fallbackIndex = Number(
            slide.dataset?.swiperSlideIndex || index
          );
          const swiperSlideIndex = findIndex !== -1 ? findIndex : fallbackIndex;
          sliderEl.swiper.slideTo(swiperSlideIndex, 800);
        }
      });
    }

    // for stacked and grid layout
    const stackedOrGridEl =
      mediaEl.querySelector(
        ".product__media-list[data-desktop-type='stacked']"
      ) ||
      mediaEl.querySelector(".product__media-list[data-desktop-type='grid']") ||
      mediaEl.querySelector(".product__media-list[data-mobile-type='grid']");
    if (stackedOrGridEl) {
      stackedOrGridEl
        .querySelectorAll("[data-media-id]")
        .forEach((mediaItem) => {
          const mediaItemId = mediaItem.dataset?.mediaId;
          if (mediaItemId === mediaId) {
            mediaItem.classList.add("stacked-or-grid-active");
          } else {
            mediaItem.classList.remove("stacked-or-grid-active");
          }
        });
    }
  }

  updateElementsAfterFetch(html) {
    // attr data-original-section use for Quick view modal
    const currentSectionId = this.dataset.section;
    const sourceSectionId = this.dataset.originalSection
      ? this.dataset.originalSection
      : this.dataset.section;

    // Price
    const priceDestination = document.querySelectorAll(
      `[data-id="Price-${currentSectionId}"]`
    );
    const priceSource = html.querySelector(
      `[data-id="Price-${sourceSectionId}"]`
    );
    if (priceSource) {
      priceDestination.forEach((el) => {
        el.innerHTML = priceSource.innerHTML;
        el.classList.remove("visibility-hidden");
      });
    }

    const priceStickyDestination = document.querySelectorAll(
      `[data-id="PriceSticky-${currentSectionId}"]`
    );
    const priceStickySource = html.querySelector(
      `[data-id="PriceSticky-${sourceSectionId}"]`
    );
    if (priceStickySource) {
      priceStickyDestination.forEach((el) => {
        el.innerHTML = priceStickySource.innerHTML;
        el.classList.remove("visibility-hidden");
      });
    }

    // Inventory
    const inventorySource = html.querySelector(
      `[data-id="Inventory-${sourceSectionId}"]`
    );
    const inventoryDestination = document.querySelectorAll(
      `[data-id="Inventory-${currentSectionId}"]`
    );
    if (inventorySource) {
      inventoryDestination.forEach((el) => {
        el.innerHTML = inventorySource.innerHTML;
        el.classList.toggle(
          "visibility-hidden",
          inventorySource.innerText === ""
        );
      });
    }

    // SKU
    const skuSource = html.querySelector(`[data-id="Sku-${sourceSectionId}"]`);
    const skuDestination = document.querySelectorAll(
      `[data-id="Sku-${currentSectionId}"]`
    );
    if (skuSource) {
      skuDestination.forEach((el) => {
        el.innerHTML = skuSource.innerHTML;
        el.classList.toggle(
          "visibility-hidden",
          skuSource.classList.contains("visibility-hidden")
        );
      });
    }

    // Badges
    const badgesSource = html.querySelector(
      `[data-id="Badges-${sourceSectionId}"]`
    );
    const badgesDestination = document.querySelectorAll(
      `[data-id="Badges-${currentSectionId}"]`
    );
    if (badgesSource) {
      badgesDestination.forEach((el) => {
        el.innerHTML = badgesSource.innerHTML;
      });
    }

    // Title: sale badge
    const titleBadgeSource = html.querySelector(
      `[data-id="TitleBadge-${sourceSectionId}"]`
    );
    const titleBadgeDestination = document.querySelectorAll(
      `[data-id="TitleBadge-${currentSectionId}"]`
    );
    if (titleBadgeSource) {
      titleBadgeDestination.forEach((el) => {
        el.classList.toggle(
          "visibility-hidden",
          titleBadgeSource.classList.contains("visibility-hidden")
        );
      });
    }

    // Variant picker: Color swatches label
    const colorNameSources = html.querySelectorAll(
      `[data-id^="ColorName-${sourceSectionId}"]`
    );
    const colorNameDestinations = document.querySelectorAll(
      `[data-id^="ColorName-${currentSectionId}"]`
    );
    if (colorNameSources?.length === colorNameDestinations?.length) {
      colorNameDestinations.forEach((colorNameDestination, index) => {
        colorNameDestination.classList.toggle(
          "visibility-hidden",
          colorNameSources[index].classList.contains("visibility-hidden")
        );
        colorNameDestination.innerHTML = colorNameSources[index].innerHTML;
      });
    }

    // Variant picker: Options
    const variantPickerSource = html.querySelector(
      `[data-id="VariantPicker-${sourceSectionId}"]`
    );
    const variantPickerDestinations = document.querySelectorAll(
      `[data-id="VariantPicker-${currentSectionId}"]`
    );
    if (variantPickerSource) {
      const quickViewModal = this.closest("quick-view-modal");
      const sourceOptionValues = variantPickerSource.querySelectorAll(
        `[data-id^="OptionValues-${sourceSectionId}-"]`
      );
      const sourceCurrentVariantScript = variantPickerSource.querySelector(
        "[data-selected-variant]"
      );

      if (sourceOptionValues.length && !this.connectedProductURL) {
        variantPickerDestinations.forEach((pickerDestEl) => {
          sourceOptionValues.forEach((sourceOptionsEl) => {
            const position = sourceOptionsEl.dataset.optionPosition;
            if (!position) return;
            const destOptionsEl = pickerDestEl.querySelector(
              `[data-option-position="${position}"][data-id^="OptionValues-"]`
            );
            if (destOptionsEl) {
              destOptionsEl.innerHTML = quickViewModal
                ? sourceOptionsEl.innerHTML.replaceAll(
                    sourceSectionId,
                    `quickview-${sourceSectionId}`
                  )
                : sourceOptionsEl.innerHTML;
            }
          });

          if (sourceCurrentVariantScript) {
            const destCurrentVariantScript = pickerDestEl.querySelector(
              "[data-selected-variant]"
            );
            if (destCurrentVariantScript) {
              destCurrentVariantScript.innerHTML =
                sourceCurrentVariantScript.innerHTML;
            }
          }
        });
      } else {
        // fallback to replace whole variant picker html
        if (quickViewModal) {
          variantPickerDestinations.forEach((el) => {
            el.innerHTML = variantPickerSource.innerHTML.replaceAll(
              sourceSectionId,
              `quickview-${sourceSectionId}`
            );
          });
        } else {
          variantPickerDestinations.forEach((el) => {
            el.innerHTML = variantPickerSource.innerHTML;
          });
        }
      }
    }

    // Update elements for B2B quantity pricing https://help.shopify.com/en/manual/b2b/catalogs/quantity-pricing
    const productButtonsSource = html.querySelector(
      `[data-id="ProductButtons-${sourceSectionId}"]`
    );
    const productButtonsDestination = document.querySelectorAll(
      `[data-id="ProductButtons-${currentSectionId}"]`
    );
    if (productButtonsSource) {
      const sourceButtonsWrapper = productButtonsSource.querySelector(
        ".product-form__buttons"
      );
      productButtonsDestination.forEach((el) => {
        const buttonsWrapper = el.querySelector(".product-form__buttons");

        if (sourceButtonsWrapper && buttonsWrapper) {
          const sourceHasQuantityPricing =
            sourceButtonsWrapper.dataset.hasQuantityPricing === "true";
          const destHadQuantityPricing =
            buttonsWrapper.dataset.hasQuantityPricing === "true";

          // This function is executed if quantity pricing is configured for the product or product variant
          if (sourceHasQuantityPricing || destHadQuantityPricing) {
            if (sourceHasQuantityPricing) {
              buttonsWrapper.dataset.hasQuantityPricing = "true";
            } else {
              buttonsWrapper.removeAttribute("data-has-quantity-pricing");
            }

            // Update b2b elements (volume pricing + quantity rules)
            const sourceB2bEl = sourceButtonsWrapper.querySelector(
              ".product-b2b-elements"
            );
            const destB2bEl = buttonsWrapper.querySelector(
              ".product-b2b-elements"
            );
            if (sourceB2bEl && destB2bEl) {
              destB2bEl.innerHTML = sourceB2bEl.innerHTML;
            } else if (sourceB2bEl && !destB2bEl) {
              buttonsWrapper.insertAdjacentHTML(
                "afterbegin",
                sourceB2bEl.outerHTML
              );
            } else if (!sourceB2bEl && destB2bEl) {
              destB2bEl.remove();
            }

            // Update quantity input
            const sourceQuantityWrapper = sourceButtonsWrapper.querySelector(
              ".product-form__quantity"
            );
            const destQuantityWrapper = buttonsWrapper.querySelector(
              ".product-form__quantity"
            );
            if (sourceQuantityWrapper && destQuantityWrapper) {
              const quickViewModal = this.closest("quick-view-modal");
              destQuantityWrapper.innerHTML = quickViewModal
                ? sourceQuantityWrapper.innerHTML.replaceAll(
                    sourceSectionId,
                    `quickview-${sourceSectionId}`
                  )
                : sourceQuantityWrapper.innerHTML;
            }
          }
        }
      });
    }

    // Product media
    // Product media is replaced with DOM changes and re-initialization of initProductMediaGallery only when Variant media display is not set to "Show all" or when a product is linked to the variant (e.g., via Combined listings).
    // In most other cases, it updates via updateMedia without DOM changes.
    if (this.variantMediaDisplay !== "show_all" || this.connectedProductURL) {
      const mediaSource = html.querySelector(
        `[data-section="product-media-${sourceSectionId}"]`
      );
      const mediaDestination = document.querySelector(
        `[data-section="product-media-${currentSectionId}"]`
      );
      if (mediaSource && mediaDestination) {
        mediaDestination.className = mediaSource.className;
        mediaDestination.dataset.variantMediaDisplay =
          mediaSource.dataset.variantMediaDisplay;
        mediaDestination.dataset.desktopType = mediaSource.dataset.desktopType;
        mediaDestination.dataset.mobileType = mediaSource.dataset.mobileType;
        mediaDestination.innerHTML = mediaSource.innerHTML;

        const parentQuickView = this.closest("quick-view-modal");
        if (parentQuickView) {
          if (typeof parentQuickView.removeDOMElements === "function") {
            parentQuickView.removeDOMElements(mediaDestination);
          }
          if (typeof parentQuickView.clearMediaClasses === "function") {
            parentQuickView.clearMediaClasses();
          }
          if (typeof parentQuickView.initSlider === "function") {
            parentQuickView.initSlider();
          }
        } else {
          // Update zoom modal for Combined listings that was moved to body by ModalDialog.connectedCallback
          if (this.connectedProductURL) {
            const zoomModalSource = html.querySelector(
              `#ProductModal-${sourceSectionId}`
            );
            const zoomModalDest = document.querySelector(
              `#ProductModal-${currentSectionId}`
            );

            if (zoomModalSource && zoomModalDest) {
              const newZoomModal = zoomModalSource.cloneNode(true);
              newZoomModal.id = `ProductModal-${currentSectionId}`;
              zoomModalDest.replaceWith(newZoomModal);
            } else if (zoomModalSource && !zoomModalDest) {
              const newZoomModal = zoomModalSource.cloneNode(true);
              newZoomModal.id = `ProductModal-${currentSectionId}`;
              mediaDestination.after(newZoomModal);
            } else if (!zoomModalSource && zoomModalDest) {
              zoomModalDest.remove();
            }
          }

          const section = document.getElementById(
            `shopify-section-${currentSectionId}`
          );

          if (section && typeof window.initProductMediaGallery === "function") {
            window.initProductMediaGallery(section);
          }
        }
      }
    }

    // Replace other elements for Combined listings
    if (this.connectedProductURL) {
      // ...Src - source elements from response HTML
      // ...Dest - destination elements in the current DOM
      const productInfoSrc = html.getElementById(
        `ProductInfo-${sourceSectionId}`
      );
      const productInfoDest = document.getElementById(
        `ProductInfo-${currentSectionId}`
      );
      if (productInfoSrc && productInfoDest) {
        // Title
        const titleSrc = productInfoSrc.querySelector(".product__title");
        const titleDest = productInfoDest.querySelectorAll(".product__title");
        if (titleSrc) {
          titleDest.forEach((el) => {
            el.innerHTML = titleSrc.innerHTML;
          });
        }

        // Vendor
        const vendorSrc = productInfoSrc.querySelector(".product__vendor");
        const vendorDest = productInfoDest.querySelectorAll(".product__vendor");
        if (vendorSrc) {
          vendorDest.forEach((el) => {
            el.innerHTML = vendorSrc.innerHTML;
          });
        }

        // Description
        const descriptionSrc = productInfoSrc.querySelector(
          ".product__description"
        );
        const descriptionDest = productInfoDest.querySelectorAll(
          ".product__description"
        );
        if (descriptionSrc) {
          descriptionDest.forEach((el) => {
            el.innerHTML = descriptionSrc.innerHTML;
            el.classList.toggle(
              "hidden",
              descriptionSrc.classList.contains("hidden")
            );
          });
        }

        // Badges
        const badgesSrc = productInfoSrc.querySelector(".product__badges");
        const badgesDest = productInfoDest.querySelectorAll(".product__badges");
        if (badgesSrc) {
          badgesDest.forEach((el) => {
            el.innerHTML = badgesSrc.innerHTML;
          });
        }

        // Complementary products
        const complementaryProductsSrc = productInfoSrc.querySelector(
          "product-recommendations.complementary-products"
        );
        const complementaryProductsDest = productInfoDest.querySelectorAll(
          "product-recommendations.complementary-products"
        );
        if (complementaryProductsSrc) {
          complementaryProductsDest.forEach((el) => {
            el.replaceWith(complementaryProductsSrc.cloneNode(true));
          });
        }
      }

      const giftCardRecipientSrc = html.querySelector(
        `[data-id="GiftCardRecipient-${sourceSectionId}"]`
      );
      const giftCardRecipientDest = document.querySelectorAll(
        `[data-id="GiftCardRecipient-${currentSectionId}"]`
      );
      if (giftCardRecipientSrc) {
        giftCardRecipientDest.forEach((el) => {
          el.innerHTML = giftCardRecipientSrc.innerHTML;
        });

        const quickViewModalEl = this.closest("quick-view-modal");
        if (quickViewModalEl) {
          const infoContainer = quickViewModalEl.querySelector(
            ".product__info-container"
          );
          giftCardRecipientDest.forEach((el) => {
            const recipientForm = el.querySelector("recipient-form");
            if (recipientForm && infoContainer) {
              infoContainer.appendChild(recipientForm);
            }
          });
        }
      }

      const stickyControlsSrc = html.querySelector(
        `[data-id="StickyControls-${sourceSectionId}"]`
      );
      const stickyControlsDest = document.querySelectorAll(
        `[data-id="StickyControls-${currentSectionId}"]`
      );
      if (stickyControlsSrc) {
        stickyControlsDest.forEach((el) => {
          el.innerHTML = stickyControlsSrc.innerHTML;
        });
      }

      const stickyImageSrc = html.querySelector(
        `[data-id="StickyImage-${sourceSectionId}"]`
      );
      const stickyImageDest = document.querySelectorAll(
        `[data-id="StickyImage-${currentSectionId}"]`
      );
      if (stickyImageSrc) {
        stickyImageDest.forEach((el) => {
          el.innerHTML = stickyImageSrc.innerHTML;
          el.classList.toggle(
            "hidden",
            stickyImageSrc.classList.contains("hidden")
          );
        });
      }

      const stickyTitleSrc = html.querySelector(
        `[data-id="StickyTitle-${sourceSectionId}"]`
      );
      const stickyTitleDest = document.querySelectorAll(
        `[data-id="StickyTitle-${currentSectionId}"]`
      );
      if (stickyTitleSrc) {
        stickyTitleDest.forEach((el) => {
          el.innerHTML = stickyTitleSrc.innerHTML;
        });
      }

      const backInStockDrawer = document.getElementById(
        `BackInStockAlertDrawer-${currentSectionId}`
      );
      if (
        backInStockDrawer &&
        typeof backInStockDrawer.updateProduct === "function"
      ) {
        const titleSrc = productInfoSrc?.querySelector(".product__title");
        backInStockDrawer.updateProduct({
          title: titleSrc ? titleSrc.textContent.trim() : null,
          url: this.connectedProductURL,
        });
      }
    }
  }

  toggleAddButton(disable = true, text) {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, #sticky-bar-product-form-${this.dataset.section}`
    );
    productForms.forEach((productForm) => {
      const addButton = productForm.querySelector('[name="add"]');
      if (!addButton) return;

      const addButtonText = addButton.querySelector(".js-change-button-text");

      if (disable) {
        addButton.setAttribute("disabled", true);
        addButton.setAttribute("aria-disabled", true);
        if (text && addButtonText) {
          addButtonText.textContent = text;
          addButtonText.dataset.label = text;

          if (text === window.variantStrings.unavailable) {
            addButton.dataset.status = "unavailable";
          } else {
            addButton.dataset.status = "sold-out";
          }
        }
      } else {
        addButton.removeAttribute("disabled");
        addButton.removeAttribute("aria-disabled");
        if (addButtonText) {
          addButtonText.textContent = window.variantStrings.addToCart;
          addButtonText.dataset.label = window.variantStrings.addToCart;
        }
        addButton.dataset.status = "available";
      }
    });
  }

  setUnavailable() {
    const price = document.querySelectorAll(
      `[data-id="Price-${this.dataset.section}"]`
    );
    const priceSticky = document.querySelectorAll(
      `[data-id="PriceSticky-${this.dataset.section}"]`
    );
    const inventory = document.querySelectorAll(
      `[data-id="Inventory-${this.dataset.section}"]`
    );
    const sku = document.querySelectorAll(
      `[data-id="Sku-${this.dataset.section}"]`
    );
    const colorName = document.querySelectorAll(
      `[data-id^="ColorName-${this.dataset.section}"]`
    );

    this.toggleAddButton(true, window.variantStrings.unavailable);

    price.forEach((el) => el.classList.add("visibility-hidden"));
    priceSticky.forEach((el) => el.classList.add("visibility-hidden"));
    inventory.forEach((el) => el.classList.add("visibility-hidden"));
    sku.forEach((el) => el.classList.add("visibility-hidden"));
    colorName.forEach((el) => el.classList.add("visibility-hidden"));
  }

  resetErrorMessage() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, #sticky-bar-product-form-${this.dataset.section}`
    );
    productForms.forEach((productForm) => {
      const parentEl =
        productForm.closest(".product-sticky-add-bar") ||
        productForm.closest("product-form");
      if (parentEl) {
        const errorWrapperEl = parentEl.querySelector(
          ".product-form__error-message-wrapper"
        );
        const errorTextEl = errorWrapperEl?.querySelector(
          ".product-form__error-message"
        );
        if (!errorWrapperEl || !errorTextEl) return;
        errorWrapperEl.setAttribute("hidden", true);
        errorTextEl.textContent = "";
      }
    });
  }
}

if (!customElements.get("variant-picker")) {
  customElements.define("variant-picker", VariantPicker);
}

class ProductCard extends HTMLElement {
  constructor() {
    super();
    this._initialized = false;
    this._onInputChange = this.onInputChange.bind(this);
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    this.addEventListener("change", this._onInputChange);
    this.initQuickAddHoverState();
    this.initMediaSlider();
    this.syncCardState();
  }

  disconnectedCallback() {
    this.removeEventListener("change", this._onInputChange);
    this.destroyQuickAddHoverState();
    this.destroyMediaSlider();
    this._initialized = false;
  }

  initMediaSlider() {
    this.mediaSliderContainer = this.querySelector(
      ".product-card__media--has-slider"
    );
    if (!this.mediaSliderContainer) return;

    this.mediaSlides = [
      ...this.querySelectorAll(".product-card__media-item--slide"),
    ];
    if (this.mediaSlides.length <= 1) return;

    this.mediaSliderIndex = this.mediaSlides.findIndex((slide) =>
      slide.classList.contains("is-active")
    );
    if (this.mediaSliderIndex < 0) this.mediaSliderIndex = 0;

    this.mediaPrevButton = this.querySelector(
      ".product-card__slider-arrow--prev"
    );
    this.mediaNextButton = this.querySelector(
      ".product-card__slider-arrow--next"
    );
    if (!this.mediaPrevButton || !this.mediaNextButton) return;

    // Before Swiper takes over, the arrows still work through the plain
    // crossfade toggle below; once mediaSwiper exists its own navigation
    // listeners (wired in setupMediaSwiper) own these same buttons.
    this._onMediaSliderPrev = (event) => {
      if (this.mediaSwiper) return;
      event.preventDefault();
      event.stopPropagation();
      this.goToMediaSlide(this.mediaSliderIndex - 1);
    };
    this._onMediaSliderNext = (event) => {
      if (this.mediaSwiper) return;
      event.preventDefault();
      event.stopPropagation();
      this.goToMediaSlide(this.mediaSliderIndex + 1);
    };

    this.mediaPrevButton.addEventListener("click", this._onMediaSliderPrev);
    this.mediaNextButton.addEventListener("click", this._onMediaSliderNext);

    // Swiper (touch/wheel/navigation) is only built once the card is about
    // to enter the viewport, so a grid of dozens of cards doesn't pay for
    // dozens of Swiper instances that may never be seen.
    this._mediaSliderObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this._mediaSliderObserver.disconnect();
        this._mediaSliderObserver = null;
        this.setupMediaSwiper();
      },
      { rootMargin: "200px" }
    );
    this._mediaSliderObserver.observe(this.mediaSliderContainer);
  }

  setupMediaSwiper() {
    if (this.mediaSwiper || !this.isConnected || typeof Swiper === "undefined") {
      return;
    }

    const wrapperEl = document.createElement("div");
    wrapperEl.className = "swiper-wrapper";
    this.mediaSlides.forEach((slide) => {
      slide.classList.add("swiper-slide");
      wrapperEl.appendChild(slide);
    });
    this.mediaSliderContainer.prepend(wrapperEl);
    this.mediaSliderContainer.classList.add("swiper");

    this.mediaSwiper = new Swiper(this.mediaSliderContainer, {
      slidesPerView: 1,
      loop: true,
      speed: 500,
      allowTouchMove: true,
      // The full-card ".product-card__media-link" overlay sits on top of
      // (outside) the swiper-wrapper for click-through navigation, so
      // Swiper's default wrapper-scoped touch target would never see a
      // touch/pointer that starts on it. Listen on the whole container instead.
      touchEventsTarget: "container",
      initialSlide: this.mediaSliderIndex,
      mousewheel: {
        forceToAxis: true,
      },
      navigation: {
        nextEl: this.mediaNextButton,
        prevEl: this.mediaPrevButton,
        disabledClass: "swiper-button-disabled",
      },
      on: {
        slideChange: (swiper) => {
          this.mediaSliderIndex = swiper.realIndex;
        },
      },
    });
  }

  goToMediaSlide(index) {
    const count = this.mediaSlides.length;
    const nextIndex = ((index % count) + count) % count;

    if (this.mediaSwiper) {
      this.mediaSwiper.slideToLoop(nextIndex);
      this.mediaSliderIndex = nextIndex;
      return;
    }

    if (nextIndex === this.mediaSliderIndex) return;
    this.mediaSlides[this.mediaSliderIndex]?.classList.remove("is-active");
    this.mediaSlides[nextIndex]?.classList.add("is-active");
    this.mediaSliderIndex = nextIndex;
  }

  destroyMediaSlider() {
    if (this.mediaPrevButton && this._onMediaSliderPrev) {
      this.mediaPrevButton.removeEventListener(
        "click",
        this._onMediaSliderPrev
      );
    }
    if (this.mediaNextButton && this._onMediaSliderNext) {
      this.mediaNextButton.removeEventListener(
        "click",
        this._onMediaSliderNext
      );
    }
    if (this._mediaSliderObserver) {
      this._mediaSliderObserver.disconnect();
      this._mediaSliderObserver = null;
    }
    if (this.mediaSwiper) {
      this.mediaSwiper.destroy(true, true);
      this.mediaSwiper = null;
    }
  }

  initQuickAddHoverState() {
    this.informationBottom = this.querySelector(
      ".product-card__information-bottom"
    );
    this.quickAddButton = this.querySelector(
      ".product-card__quickadd .product-card__button"
    );
    if (!this.informationBottom || !this.quickAddButton) return;

    this._onQuickAddEnter = () => {
      this.informationBottom.classList.add("is-options-open");
    };
    this._onQuickAddLeave = () => {
      this.informationBottom.classList.remove("is-options-open");
    };
    this._onQuickAddFocus = () => {
      this.informationBottom.classList.add("is-options-open");
    };
    this._onQuickAddBlur = () => {
      if (!this.informationBottom.contains(document.activeElement)) {
        this.informationBottom.classList.remove("is-options-open");
      }
    };
    this._onInfoLeave = () => {
      this.informationBottom.classList.remove("is-options-open");
    };
    this._onInfoMove = (event) => {
      if (event.target.closest(".product-card__quickadd")) {
        this.informationBottom.classList.add("is-options-open");
      }
    };

    this.quickAddButton.addEventListener("mouseenter", this._onQuickAddEnter);
    this.quickAddButton.addEventListener("mouseleave", this._onQuickAddLeave);
    this.quickAddButton.addEventListener("focus", this._onQuickAddFocus);
    this.quickAddButton.addEventListener("blur", this._onQuickAddBlur);
    this.informationBottom.addEventListener("mouseleave", this._onInfoLeave);
    this.informationBottom.addEventListener("mouseover", this._onInfoMove);
  }

  destroyQuickAddHoverState() {
    if (!this.informationBottom || !this.quickAddButton) return;
    this.quickAddButton.removeEventListener(
      "mouseenter",
      this._onQuickAddEnter
    );
    this.quickAddButton.removeEventListener(
      "mouseleave",
      this._onQuickAddLeave
    );
    this.quickAddButton.removeEventListener("focus", this._onQuickAddFocus);
    this.quickAddButton.removeEventListener("blur", this._onQuickAddBlur);
    this.informationBottom.removeEventListener("mouseleave", this._onInfoLeave);
    this.informationBottom.removeEventListener("mouseover", this._onInfoMove);
  }

  onInputChange(event) {
    const input = event.target;
    if (!input || input.type !== "radio") return;
    this.syncCardState(input);
  }

  getVariants() {
    const variantsEl = this.querySelector("[data-product-card-variants]");
    if (!variantsEl) return [];
    try {
      return JSON.parse(variantsEl.textContent || "[]");
    } catch (error) {
      return [];
    }
  }

  getFeaturedMediaFallback() {
    const featuredMediaEl = this.querySelector(
      "[data-product-card-featured-media]"
    );
    if (!featuredMediaEl) return null;
    try {
      return JSON.parse(featuredMediaEl.textContent || "null");
    } catch (error) {
      return null;
    }
  }

  getSelectedOptionValues() {
    const groups = [
      ...this.querySelectorAll(".js-card-option-group[data-option-position]"),
    ];
    const selected = new Map();

    groups.forEach((group) => {
      const position = Number(group.dataset.optionPosition || 0);
      if (!position) return;
      const checkedInput = group.querySelector("input[type='radio']:checked");
      if (!checkedInput) return;
      selected.set(position, checkedInput.value);
    });

    return selected;
  }

  findVariantBySelections(variants, selectedMap) {
    if (!variants.length || !selectedMap.size) return null;
    return (
      variants.find((variant) => {
        if (!variant || !Array.isArray(variant.options)) return false;
        for (const [position, value] of selectedMap.entries()) {
          if (variant.options[position - 1] !== value) return false;
        }
        return true;
      }) || null
    );
  }

  findFallbackVariant(variants, changedInput) {
    if (!changedInput) return null;
    const variantId = Number(changedInput.dataset.variantId || 0);
    if (!variantId) return null;
    return variants.find((variant) => Number(variant.id) === variantId) || null;
  }

  normalizeImageUrl(url) {
    if (!url || typeof url !== "string") return "";
    if (url.startsWith("//")) return `${window.location.protocol}${url}`;
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    if (url.startsWith("/")) return `${window.location.origin}${url}`;
    return url;
  }

  getVariantImage(variant, fallbackMedia) {
    if (variant?.featured_media?.preview_image?.src) {
      return {
        src: variant.featured_media.preview_image.src,
        alt: variant.featured_media.alt || variant.title || "",
      };
    }

    if (variant?.featured_image?.src) {
      return {
        src: variant.featured_image.src,
        alt: variant.featured_image.alt || variant.title || "",
      };
    }

    if (fallbackMedia?.preview_image?.src) {
      return {
        src: fallbackMedia.preview_image.src,
        alt: fallbackMedia.alt || "",
      };
    }

    if (fallbackMedia?.src) {
      return {
        src: fallbackMedia.src,
        alt: fallbackMedia.alt || "",
      };
    }

    return null;
  }

  getVariantMediaId(variant) {
    return (
      variant?.featured_media?.preview_image?.id ||
      variant?.featured_image?.id ||
      variant?.featured_media?.id ||
      null
    );
  }

  goToSlideForVariant(variant) {
    if (!this.mediaSlides || this.mediaSlides.length <= 1) return false;

    const mediaId = this.getVariantMediaId(variant);
    if (!mediaId) return false;

    const index = this.mediaSlides.findIndex(
      (slide) => Number(slide.dataset.mediaId) === Number(mediaId)
    );
    if (index < 0) return false;

    this.goToMediaSlide(index);
    return true;
  }

  updateImage(variant) {
    if (this.goToSlideForVariant(variant)) return;

    const primaryImage =
      this.querySelector(
        ".product-card__media .product-card__media-item.is-active"
      ) ||
      this.querySelector(
        ".product-card__media .product-card__media-item:not(.product-card__media-item--second)"
      );
    if (!primaryImage || primaryImage.tagName.toLowerCase() !== "img") return;

    const imageData = this.getVariantImage(
      variant,
      this.getFeaturedMediaFallback()
    );
    if (!imageData?.src) return;

    const normalizedUrl = this.normalizeImageUrl(imageData.src);
    if (!normalizedUrl) return;

    primaryImage.src = normalizedUrl;
    primaryImage.srcset = `${normalizedUrl} 1x`;
    if (imageData.alt) {
      primaryImage.alt = imageData.alt;
    }
  }

  updateQuickAddVariant(variant) {
    const variantInput = this.querySelector(
      ".product-card__quickadd input[name='id']"
    );
    if (!variantInput || !variant?.id) return;
    variantInput.value = variant.id;
    variantInput.dispatchEvent(new Event("change", { bubbles: true }));
  }

  updateQuickAddButton(variant) {
    const button = this.querySelector(
      ".product-card__quickadd button[name='add']"
    );
    if (!button) return;

    const quickAddText = button.querySelector(".product-card__quickadd-text");
    const soldOutMessage = button.querySelector(".sold-out-message");
    const quickAddLabel =
      button.dataset.quickAddLabel ||
      window.variantStrings?.addToCart ||
      "Add to cart";
    const soldOutLabel =
      button.dataset.soldOutLabel ||
      window.variantStrings?.soldOut ||
      "Sold out";
    const isAvailable = Boolean(variant && variant.available);
    const quickAddIconWrap = button.querySelector(
      ".product-card__quick-add-icon"
    );

    if (isAvailable) {
      button.removeAttribute("disabled");
      button.removeAttribute("aria-disabled");
      button.classList.add("product-card__button--quick-add");
      if (quickAddText) {
        quickAddText.textContent = quickAddLabel;
        quickAddText.dataset.label = quickAddLabel;
        quickAddText.classList.remove("hidden");
      }
      if (soldOutMessage) soldOutMessage.classList.add("hidden");
      if (quickAddIconWrap) quickAddIconWrap.hidden = false;
    } else {
      button.setAttribute("disabled", "disabled");
      button.setAttribute("aria-disabled", "true");
      button.classList.remove("product-card__button--quick-add");
      if (quickAddText) quickAddText.classList.add("hidden");
      if (soldOutMessage) {
        soldOutMessage.textContent = soldOutLabel;
        soldOutMessage.dataset.label = soldOutLabel;
        soldOutMessage.classList.remove("hidden");
      }
      if (quickAddIconWrap) quickAddIconWrap.hidden = true;
    }
  }

  resetErrorMessage() {
    const errorEl = this.querySelector(".product-card__error");
    if (!errorEl) return;
    errorEl.setAttribute("hidden", true);
    errorEl.textContent = "";
  }

  updateProductUrls(variant) {
    const productUrl = this.dataset.productUrl || "";
    if (!productUrl) return;

    let nextUrl = productUrl;
    if (variant?.id) {
      nextUrl = `${productUrl}${productUrl.includes("?") ? "&" : "?"}variant=${variant.id}`;
    }

    this.querySelectorAll("[data-product-url]").forEach((element) => {
      element.dataset.productUrl = nextUrl;
    });

    this.querySelectorAll(
      "a.product-card__media-link, .product-card__name a"
    ).forEach((link) => {
      if (link && link.tagName.toLowerCase() === "a") {
        link.setAttribute("href", nextUrl);
      }
    });
  }

  updateOptionAvailability(variants, selectedMap) {
    const groups = [
      ...this.querySelectorAll(".js-card-option-group[data-option-position]"),
    ];
    groups.forEach((group) => {
      const position = Number(group.dataset.optionPosition || 0);
      if (!position) return;
      const inputs = [...group.querySelectorAll("input[type='radio']")];
      inputs.forEach((input) => {
        const tempSelections = new Map(selectedMap);
        tempSelections.set(position, input.value);
        const hasAnyVariant = variants.some((variant) => {
          if (!variant || !variant.available || !Array.isArray(variant.options))
            return false;
          for (const [
            selectedPosition,
            selectedValue,
          ] of tempSelections.entries()) {
            if (variant.options[selectedPosition - 1] !== selectedValue)
              return false;
          }
          return true;
        });
        input.classList.toggle("disabled", !hasAnyVariant);
      });
    });
  }

  syncCardState(changedInput = null) {
    const variants = this.getVariants();
    if (!variants.length) return;

    const selectedMap = this.getSelectedOptionValues();
    let matchedVariant = this.findVariantBySelections(variants, selectedMap);
    if (!matchedVariant) {
      matchedVariant = this.findFallbackVariant(variants, changedInput);
    }
    if (!matchedVariant) return;

    this.updateQuickAddVariant(matchedVariant);
    this.updateQuickAddButton(matchedVariant);
    this.updateProductUrls(matchedVariant);
    this.updateImage(matchedVariant);
    this.updateOptionAvailability(variants, selectedMap);
    this.resetErrorMessage();
  }
}

if (!customElements.get("product-card")) {
  customElements.define("product-card", ProductCard);
}

class CollectionCard extends HTMLElement {
  constructor() {
    super();
    this._isSwapped = false;
    this._onEnter = this.onEnter.bind(this);
  }

  connectedCallback() {
    this.secondaryLayer = this.querySelector(
      ".collection-card__media-layer--secondary"
    );
    if (!this.secondaryLayer) return;
    this.addEventListener("mouseenter", this._onEnter);
  }

  disconnectedCallback() {
    this.removeEventListener("mouseenter", this._onEnter);
  }

  onEnter() {
    this._isSwapped = !this._isSwapped;
    this.classList.toggle("is-swapped", this._isSwapped);
  }
}

if (!customElements.get("collection-card")) {
  customElements.define("collection-card", CollectionCard);
}

class VariantDropdownSelect extends HTMLElement {
  constructor() {
    super();

    this.currentEl = this.querySelector(".dropdown-select__current");
    this.optionsWrapperEl = this.querySelector(".dropdown-select__options");
    this.hiddenInput = this.querySelector("input[type='hidden']");
    if (!this.currentEl || !this.optionsWrapperEl || !this.hiddenInput) return;

    this.optionsEls = Array.from(this.optionsWrapperEl.querySelectorAll("li"));
    this.inputs = Array.from(this.optionsWrapperEl.querySelectorAll("input"));
    this.hasColorSwatch = this.currentEl?.classList?.contains(
      "dropdown-select__current--with-color"
    );
    this.isActive = false;

    this.optionPosition = this.getOptionPosition();
    this._onCurrentClick = this.onCurrentClick.bind(this);
    this._onKeyUp = this.onKeyUp.bind(this);
    this._onOptionsClick = this.onClickOption.bind(this);
    this._onKeyUpOptions = this.onKeyUpOptions.bind(this);
    this._onOutsideClick = this.onOutsideClick.bind(this);
  }

  connectedCallback() {
    this.currentEl.addEventListener("click", this._onCurrentClick);
    this.currentEl.addEventListener("keyup", this._onKeyUp);
    this.currentEl.addEventListener("dblclick", this.preventDoubleClick);
    this.optionsWrapperEl.addEventListener("click", this._onOptionsClick);
    this.optionsWrapperEl.addEventListener("keyup", this._onKeyUpOptions);
    document.addEventListener("click", this._onOutsideClick);
  }

  disconnectedCallback() {
    this.currentEl.removeEventListener("click", this._onCurrentClick);
    this.currentEl.removeEventListener("keyup", this._onKeyUp);
    this.currentEl.removeEventListener("dblclick", this.preventDoubleClick);
    this.optionsWrapperEl.removeEventListener("click", this._onOptionsClick);
    this.optionsWrapperEl.removeEventListener("keyup", this._onKeyUpOptions);
    document.removeEventListener("click", this._onOutsideClick);
  }

  onCurrentClick(event) {
    event.stopPropagation();
    if (this.isActive) {
      this.onClose();
    } else {
      this.onOpen();
    }
  }

  onKeyUp(event) {
    if (event.code?.toUpperCase() === "ENTER") {
      event.preventDefault();
      event.stopPropagation();
      if (this.isActive) {
        this.onClose();
      } else {
        this.onOpen();
      }
    }
  }

  preventDoubleClick(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  onClickOption(event) {
    const optionEl = event.target.closest("li");
    const currentValueEl = this.currentEl.querySelector(
      "[data-dropdown-current-value]"
    );

    if (
      !optionEl ||
      !currentValueEl ||
      !this.optionsWrapperEl.contains(optionEl)
    ) {
      event.preventDefault();
      return;
    }

    const newValue = optionEl.dataset.value;

    currentValueEl.textContent = newValue;
    this.hiddenInput.value = newValue;

    if (this.hasColorSwatch) {
      const newColor = optionEl.dataset.color;
      this.hiddenInput.dataset.colorSwatch = newColor;
      this.currentEl.style.setProperty("--swatch-color", newColor);
    }

    this.hiddenInput.dispatchEvent(new Event("change", { bubbles: true }));

    this.inputs.forEach((el) => el.removeAttribute("checked"));
    if (event.target.tagName === "INPUT") {
      event.target.setAttribute("checked", "true");
    }

    this.onClose();
    this.currentEl.focus();
  }

  onKeyUpOptions(event) {
    if (event.code === "Escape" && this.isActive) {
      event.preventDefault();
      this.onClose();
      this.currentEl.focus();
    }
  }

  onOpen() {
    document.querySelectorAll("variant-dropdown-select").forEach((dropdown) => {
      if (dropdown !== this && typeof dropdown.onClose === "function") {
        dropdown.onClose();
      }
    });

    this.optionsWrapperEl.classList.add("active");
    this.currentEl.setAttribute("aria-expanded", "true");
    this.isActive = true;

    const currentIndex = this.inputs.findIndex((inp) =>
      inp.hasAttribute("checked")
    );
    this.inputs[currentIndex >= 0 ? currentIndex : 0]?.focus();
  }

  onClose() {
    this.optionsWrapperEl.classList.remove("active");
    this.currentEl.setAttribute("aria-expanded", "false");
    this.isActive = false;
  }

  onOutsideClick(event) {
    if (!this.isActive || this.contains(event.target)) return;
    this.onClose();
  }

  getOptionPosition() {
    const parentFieldset = this.closest(".product-form__controls");
    return Number(parentFieldset?.dataset?.optionPosition || -1);
  }

  updateCurrentOption(selectedValues = []) {
    // Method is used to synchronize after changes options in sticky bar.
    if (this.optionPosition === -1) return;
    const currentValueEl = this.currentEl.querySelector(
      "[data-dropdown-current-value]"
    );

    if (!currentValueEl || !selectedValues || !selectedValues.length) return;

    const newValue = selectedValues[this.optionPosition - 1];

    currentValueEl.textContent = newValue;
    this.hiddenInput.value = newValue;

    this.optionsEls.forEach((optionEl) => {
      const liDataValue = optionEl.dataset.value;
      if (newValue === liDataValue && this.hasColorSwatch) {
        const newColor = optionEl.dataset.color;
        this.hiddenInput.dataset.colorSwatch = newColor;
        this.currentEl.style.setProperty("--swatch-color", newColor);
      }
    });
  }
}

if (!customElements.get("variant-dropdown-select")) {
  customElements.define("variant-dropdown-select", VariantDropdownSelect);
}

class ProductForm extends HTMLElement {
  constructor() {
    super();
    this._initialized = false;
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    if (this.querySelector("form")) {
      this.form = this.querySelector("form");
      this.form.querySelector("[name=id]").disabled = false;
      this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
    } else {
      if (this.querySelector("[name=id]")) {
        this.querySelector("[name=id]").disabled = false;
      }
      this.querySelector("button[type=submit]")?.addEventListener(
        "click",
        this.onSubmitHandler.bind(this)
      );
    }

    this.cartUIElement =
      this.closest("cart-drawer") ||
      document.querySelector("cart-notification") ||
      document.querySelector("cart-drawer");
    this.submitButton = this.querySelector('[type="submit"]');
    if (this.cartUIElement) {
      this.submitButton.setAttribute("aria-haspopup", "dialog");
    }

    this.hideErrors = this.dataset.hideErrors === "true";
  }

  onSubmitHandler(event) {
    event.preventDefault();
    if (this.submitButton.getAttribute("aria-disabled") === "true") return;

    this.handleErrorMessage();

    this.submitButton.setAttribute("aria-disabled", true);
    this.submitButton.classList.add("loading");
    const btnSpinner =
      this.submitButton.querySelector(".button-loader") ||
      this.submitButton.querySelector(".loading-overlay__spinner");
    if (btnSpinner) btnSpinner.classList.remove("hidden");

    const config = fetchConfig("javascript");
    config.headers["X-Requested-With"] = "XMLHttpRequest";
    delete config.headers["Content-Type"];

    const formData = new FormData(this.form);
    if (!this.form) {
      formData.append("id", this.querySelector("[name=id]").value);
    }

    if (this.cartUIElement) {
      formData.append(
        "sections",
        this.cartUIElement
          .getSectionsToRender()
          .map((section) => section.section)
      );
      formData.append("sections_url", window.location.pathname);
    }
    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        // If there is a status in the response, then this is an error
        // https://shopify.dev/docs/api/ajax/reference/cart#error-responses
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: "product-form",
            productVariantId: formData.get("id"),
            errors: response.description,
            message: response.message,
          });

          // [Start theme custom event] - Cart error event
          document.dispatchEvent(
            new CustomEvent("cart:error", {
              detail: {
                errors: response.description,
                message: response.message,
              },
            })
          );
          const refreshCartUIEvent = new CustomEvent("cart:refresh");
          document.dispatchEvent(refreshCartUIEvent);
          // [End theme custom event] - Cart error event

          this.handleErrorMessage(response.description);
          this.setSubmitButtonSoldOutState();
          this.error = true;
          return;
        } else if (!this.cartUIElement) {
          window.location = window.routes.cart_url;
          return;
        }

        if (!this.error) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "product-form",
            productVariantId: formData.get("id"),
          });
        }
        this.error = false;

        // [Start theme custom event] - Variant addition event
        document.dispatchEvent(
          new CustomEvent("variant:add", {
            detail: {
              variant: {
                id: response.variant_id || formData.get("id"),
                quantity:
                  response.quantity || Number(formData.get("quantity") || 1),
                product_title: response.product_title || "",
                variant_title: response.variant_title || "",
                image: response.image || null,
              },
              formElement: this.form,
            },
          })
        );
        // [End theme custom event] - Variant addition event

        // [Start theme custom event] - Cart change event
        fetch(`${routes.cart_url}.js`)
          .then((response) => {
            return response.text();
          })
          .then((state) => {
            const parsedState = JSON.parse(state);
            document.dispatchEvent(
              new CustomEvent("cart:change", {
                detail: {
                  cart: parsedState,
                },
              })
            );
          })
          .catch((error) => {
            console.error("Error fetching cart state:", error);
          });
        // [End theme custom event] - Cart change event

        let activeElement = document.activeElement;

        // add to cart from quick-view modal
        const quickAddModal = this.closest("quick-view-modal");
        if (quickAddModal && typeof quickAddModal?.hide === "function") {
          activeElement = quickAddModal.openedBy;
          document.body.addEventListener(
            "quick-view-closed",
            () => {
              setTimeout(() => {
                this.cartUIElement.setActiveElement(activeElement);
                this.cartUIElement.renderContents(response);
              });
            },
            { once: true }
          );
          quickAddModal.hide(true);
          return;
        }

        const productCard = this.closest("product-card");
        if (productCard) {
          const link = productCard.querySelector(".product-card__media-link");
          if (link) activeElement = link;
        }

        this.cartUIElement.setActiveElement(activeElement);
        this.cartUIElement.renderContents(response);
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        if (this.cartUIElement?.closest("cart-notification")) {
          const cartDrawer = document.querySelector("cart-drawer");
          if (cartDrawer && cartDrawer.classList.contains("is-empty")) {
            cartDrawer.classList.remove("is-empty");
          }
        }

        if (
          this.cartUIElement?.closest("cart-drawer") &&
          this.cartUIElement?.classList.contains("is-empty")
        ) {
          this.cartUIElement.classList.remove("is-empty");
        }
        if (!this.error) this.submitButton.removeAttribute("aria-disabled");
        this.submitButton.classList.remove("loading");
        if (btnSpinner) btnSpinner.classList.add("hidden");
      });
  }

  setSubmitButtonSoldOutState() {
    const soldOutMessage = this.submitButton.querySelector(".sold-out-message");
    if (!soldOutMessage) return;

    const soldOutLabel =
      this.submitButton.dataset.soldOutLabel ||
      window.variantStrings?.soldOut ||
      "Sold out";

    this.submitButton.setAttribute("aria-disabled", true);
    const quickAddText = this.submitButton.querySelector(
      ".product-card__quickadd-text"
    );
    if (quickAddText) quickAddText.classList.add("hidden");
    const quickAddIcon = this.submitButton.querySelector(
      ".product-card__quick-add-icon"
    );
    if (quickAddIcon) quickAddIcon.hidden = true;
    soldOutMessage.textContent = soldOutLabel;
    soldOutMessage.dataset.label = soldOutLabel;
    soldOutMessage.classList.remove("hidden");
    this.submitButton.classList.remove("product-card__button--quick-add");
  }

  handleErrorMessage(errorMessage = false) {
    if (this.hideErrors) return;

    const stickyBarParent = this.closest(".product-sticky-add-bar");
    const horizontalProductParent = this.closest(".product-card-horizontal");
    const productCard = this.closest("product-card");
    const errorWrapperSelector = ".product-form__error-message-wrapper";

    this.errorMessageWrapper =
      this.errorMessageWrapper ||
      this.querySelector(errorWrapperSelector) ||
      stickyBarParent?.querySelector(errorWrapperSelector) ||
      horizontalProductParent?.querySelector(errorWrapperSelector) ||
      productCard?.querySelector(".product-card__error");
    if (!this.errorMessageWrapper) return;

    this.errorMessage =
      this.errorMessage ||
      this.errorMessageWrapper.querySelector(".product-form__error-message") ||
      (this.errorMessageWrapper.classList.contains("product-card__error")
        ? this.errorMessageWrapper
        : null);
    if (!this.errorMessage) return;

    if (this.errorTimeoutId) {
      clearTimeout(this.errorTimeoutId);
      this.errorTimeoutId = null;
    }

    this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

    if (errorMessage) {
      this.errorMessage.textContent = errorMessage;

      if (horizontalProductParent) {
        this.errorTimeoutId = setTimeout(() => {
          this.errorMessageWrapper.toggleAttribute("hidden", true);
          this.errorMessage.textContent = "";
          this.errorTimeoutId = null;
        }, 3000);
      }
    }
  }
}

if (!customElements.get("product-form")) {
  customElements.define("product-form", ProductForm);
}

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
    this.recommendationsSlider = null;
  }

  connectedCallback() {
    if (this._initialized) return;
    this.initProductRecommendations();
  }

  initProductRecommendations() {
    this.enableLoading();

    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("product-recommendations");
        if (recommendations && recommendations.innerHTML.trim().length) {
          //this.innerHTML = recommendations.innerHTML;
          this.replaceChildren(...recommendations.children);
        }

        if (recommendations.hasAttribute("data-section-has-card-slider")) {
          this.setAttribute("data-section-has-card-slider", "true");
          initSectionSliderLayout(this);
        } else if (recommendations.hasAttribute("data-is-complementary")) {
          this.initComplementarySlider();
        }

        if (recommendations.hasAttribute("data-products-count")) {
          this.setAttribute(
            "data-products-count",
            recommendations.getAttribute("data-products-count")
          );
        }
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.disableLoading();
        this._initialized = true;
      });
  }

  initComplementarySlider() {
    const sliderEl = this.querySelector(".js-complementary-slider");
    if (!sliderEl) return;

    const prevBtn = this.querySelector(".slider-nav-button--prev");
    const nextBtn = this.querySelector(".slider-nav-button--next");
    const paginationEl = this.querySelector(".slider-pagination");
    const controlsEl = this.querySelector(".slider-controls");
    const isSpinnerPagination = !!this.querySelector(
      ".slider-controls__pagination"
    );

    const hasAutoplay = sliderEl.getAttribute("data-autoplay") === "true";
    const pauseOnHover =
      sliderEl.getAttribute("data-autoplay-pause-on-hover") === "true";
    const autoplayDelay = Number(sliderEl.dataset.autoplayDelay || 5000);

    const sliderSettings = {
      loop: false,
      slidesPerView: 1,
      spaceBetween: 8,
      speed: 800,
      watchSlideProgress: true,
      allowTouchMove: true,
      mousewheel: {
        forceToAxis: true,
      },
      autoplay: hasAutoplay
        ? {
            delay: autoplayDelay,
            disableOnInteraction: false,
            pauseOnMouseEnter: pauseOnHover,
          }
        : false,
      navigation: {
        nextEl: nextBtn,
        prevEl: prevBtn,
        disabledClass: "swiper-button-disabled",
      },
      pagination: {
        el: paginationEl,
        clickable: true,
        ...(isSpinnerPagination
          ? {
              renderBullet(_index, className) {
                return `<span class="${className}"><svg class="bullet-spinner" viewBox="0 0 14 14" aria-hidden="true" focusable="false"><circle class="bullet-spinner__track" cx="7" cy="7" r="5"/><circle class="bullet-spinner__arc" cx="7" cy="7" r="5" transform="rotate(-90 7 7)"/></svg></span>`;
              },
            }
          : {}),
      },
      on: {
        autoplayTimeLeft(_s, _time, progress) {
          const arc = paginationEl?.querySelector(
            ".swiper-pagination-bullet-active .bullet-spinner__arc"
          );
          if (arc) arc.style.strokeDashoffset = 31.416 * progress;
        },
        slideChange() {
          paginationEl
            ?.querySelectorAll(".bullet-spinner__arc")
            .forEach((arc) => {
              arc.style.strokeDashoffset = "";
            });
        },
        autoplayPause() {
          if (controlsEl) controlsEl.setAttribute("data-autoplay-paused", "");
        },
        autoplayResume() {
          if (controlsEl) controlsEl.removeAttribute("data-autoplay-paused");
        },
      },
    };

    new Swiper(sliderEl, sliderSettings);

    if (hasAutoplay && sliderEl.swiper) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            sliderEl.swiper.autoplay.resume();
          } else {
            sliderEl.swiper.autoplay.pause();
          }
        });
      });
      observer.observe(this);
    }
  }

  enableLoading() {
    const loadingEl = this.querySelector(".product-recommendations-loading");
    if (loadingEl) loadingEl.style.display = "flex";
  }

  disableLoading() {
    const loadingEl = this.querySelector(".product-recommendations-loading");
    if (loadingEl) loadingEl.remove();
  }
}

if (!customElements.get("product-recommendations")) {
  customElements.define("product-recommendations", ProductRecommendations);
}

class LocalizationForm extends HTMLElement {
  constructor() {
    super();
    this.elements = {
      input: this.querySelector(
        'input[name="locale_code"], input[name="country_code"]'
      ),
      button: this.querySelector("button"),
      panel: this.querySelector("ul"),
    };

    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.elements.button.addEventListener("click", this.togglePanel.bind(this));
    this.addEventListener("keydown", this.onEscapePress.bind(this));

    this.querySelectorAll("a").forEach((item) =>
      item.addEventListener("click", this.onItemClick.bind(this))
    );
  }

  connectedCallback() {
    document.addEventListener("click", this.handleDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.handleDocumentClick);
  }

  handleDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.hidePanel();
    }
  }

  hidePanel() {
    this.elements.button.setAttribute("aria-expanded", "false");
    this.elements.panel.setAttribute("hidden", true);
  }

  showPanel() {
    this.elements.button.setAttribute("aria-expanded", "true");
    this.elements.panel.removeAttribute("hidden");
  }

  togglePanel() {
    if (this.elements.button.getAttribute("aria-expanded") === "true") {
      this.hidePanel();
    } else {
      this.showPanel();
    }
  }

  onEscapePress(event) {
    if (event.key === "Escape") {
      this.hidePanel();
    }
  }

  onItemClick(event) {
    event.preventDefault();
    this.elements.input.value = event.currentTarget.dataset.value;
    this.querySelector("form")?.submit();
    this.hidePanel();
  }
}

if (!customElements.get("localization-form")) {
  customElements.define("localization-form", LocalizationForm);
}

class CountdownTimer extends HTMLElement {
  constructor() {
    super();

    this.enableMockTimer =
      this.getAttribute("data-enable-mock-timer") === "true";

    if (this.enableMockTimer) {
      // get data for infinite mock timer
      const INFINITE_DAYS = Number(this.getAttribute("data-mock-end-day") || 2);
      const DIFF_HOURS = Number(this.getAttribute("data-mock-end-hour") || 3);
      const now = new Date();
      const futureDate = new Date(
        now.getTime() + (INFINITE_DAYS * 24 + DIFF_HOURS) * 60 * 60 * 1000
      );
      const year = futureDate.getFullYear();
      const month = String(futureDate.getMonth() + 1).padStart(2, "0");
      const day = String(futureDate.getDate()).padStart(2, "0");
      const hours = String(futureDate.getHours()).padStart(2, "0");
      const minutes = String(futureDate.getMinutes()).padStart(2, "0");
      this.endDate = `${year}-${month}-${day}`;
      this.endTime = `${hours}:${minutes}`;
    } else {
      // get data from section settings
      this.endDate = this.getAttribute("data-end-date");
      this.endTime = this.getAttribute("data-end-time");
    }
    // ----------------------------------------------------------------
    this.interval;
    this.isStarted = false;
    this.setInterval(this.endDate, this.endTime);
  }

  onInit(endDate, endTime) {
    this.completeBehavior = this.getAttribute("data-complete-behavior");
    this.parentEl = this.closest(".block-countdown-timer");
    this.countdownTimerEl = this.querySelector(".countdown-timer__timer");
    this.countdownEndEl = this.querySelector(".countdown-timer__end");
    this.daysEl = this.querySelector(".js-countdown-timer-days");
    this.hoursEl = this.querySelector(".js-countdown-timer-hours");
    this.minutesEl = this.querySelector(".js-countdown-timer-minutes");
    this.secondsEl = this.querySelector(".js-countdown-timer-seconds");
    // ----------------------------------------------------------------
    const countdownDate = new Date(`${endDate}T${endTime}`);
    const now = new Date();
    const distance = countdownDate.getTime() - now.getTime();
    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((distance % (1000 * 60)) / 1000);
    // ----------------------------------------------------------------
    if (distance < 0 && this.completeBehavior === "hide_block") {
      if (this.parentEl) {
        this.parentEl.style.display = "none";
      }
      this.style.display = "none";
      clearInterval(this.interval);
    } else if (distance < 0 && this.completeBehavior === "show_message") {
      if (this.countdownTimerEl) {
        this.countdownTimerEl.style.display = "none";
      }
      if (this.countdownEndEl) {
        this.countdownEndEl.style.display = "flex";
      }
      clearInterval(this.interval);
    } else {
      if (this.daysEl) {
        this.daysEl.textContent = this.getTwoDigitTime(days);
      }

      if (this.hoursEl) {
        this.hoursEl.textContent = this.getTwoDigitTime(hours);
      }

      if (this.minutesEl) {
        this.minutesEl.textContent = this.getTwoDigitTime(minutes);
      }

      if (this.secondsEl) {
        this.secondsEl.textContent = this.getTwoDigitTime(seconds);
      }
    }
  }

  getTwoDigitTime(time) {
    if (String(time).length === 1) {
      return "0" + time;
    }
    if (String(time) === "-1") {
      return "00";
    }
    return time;
  }

  setInterval(endDate, endTime) {
    clearInterval(this.interval);
    this.interval = setInterval(this.onInit.bind(this, endDate, endTime), 1000);
  }
}

if (!customElements.get("countdown-timer")) {
  customElements.define("countdown-timer", CountdownTimer);
}

(function () {
  let image = document.querySelectorAll(".data-alt img");
  image &&
    image.forEach((img, index) => {
      img.alt = "Video background";
    });
})();
